import { makeObservable, observable, action, computed } from "mobx";

export interface SnippetOverrideMetadata {
  promptName: string;
  version?: number;
  label?: string;
}

export const normalizeSnippetKey = (key: string): string => {
  const match = key.match(/@@@([^@]+?)(?:@version_number:[^@]*)?(?:@label:[^@]*)?@@@/);
  if (match) {
    return `@@@${match[1]}@@@`;
  }
  return key;
};

export const createSnippetKey = (promptName: string): string => `@@@${promptName}@@@`;

export const createFullSnippetKey = (
  promptName: string,
  version?: number,
  label?: string,
): string => {
  let key = `@@@${promptName}`;
  if (version) {
    key += `@version_number:${version}`;
  }
  if (label) {
    key += `@label:${label}`;
  }
  key += `@@@`;
  return key;
};

export const parseSnippetKey = (key: string): SnippetOverrideMetadata => {
  const match = key.match(/@@@([^@]+?)(?:@version_number:(\d+))?(?:@label:([^@]+))?@@@/);
  return {
    promptName: match?.[1] || key,
    version: match?.[2] ? Number(match[2]) : undefined,
    label: match?.[3],
  };
};

class SnippetOverrideStore {
  snippetOverrides: Record<string, string> = {};

  private _snippetMetadata: Record<string, Set<string>> = {};

  get snippetOverridesContent() {
    return JSON.stringify(this.snippetOverrides);
  }

  setSnippetOverrides(value: Record<string, string>) {
    const normalized: Record<string, string> = {};
    this._snippetMetadata = {};

    for (const [key, val] of Object.entries(value)) {
      const normalizedKey = normalizeSnippetKey(key);
      normalized[normalizedKey] = val;

      if (!this._snippetMetadata[normalizedKey]) {
        this._snippetMetadata[normalizedKey] = new Set();
      }
      this._snippetMetadata[normalizedKey].add(key);
    }

    this.snippetOverrides = normalized;
  }

  get hasOverride() {
    return Object.keys(this.snippetOverrides).length > 0;
  }

  removeSnippetOverride(key: string) {
    const normalizedKey = normalizeSnippetKey(key);
    delete this.snippetOverrides[normalizedKey];
    delete this._snippetMetadata[normalizedKey];
  }

  assignSnippetOverride(key: string, value: string) {
    const normalizedKey = normalizeSnippetKey(key);
    this.snippetOverrides[normalizedKey] = value;

    if (!this._snippetMetadata[normalizedKey]) {
      this._snippetMetadata[normalizedKey] = new Set();
    }

    this._snippetMetadata[normalizedKey].add(key);
  }

  getSnippetOverride(key: string): string | undefined {
    const normalizedKey = normalizeSnippetKey(key);
    return this.snippetOverrides[normalizedKey];
  }

  getSnippetMetadata(normalizedKey: string): SnippetOverrideMetadata[] {
    const keys = this._snippetMetadata[normalizedKey] || new Set();
    return Array.from(keys).map(parseSnippetKey);
  }

  get prompts() {
    return Object.keys(this.snippetOverrides).map(k => k.replace(/@@@/g, ""));
  }

  get overridesWithMetadata(): Record<
    string,
    { value: string; variants: SnippetOverrideMetadata[] }
  > {
    const result: Record<string, { value: string; variants: SnippetOverrideMetadata[] }> = {};

    for (const [normalizedKey, value] of Object.entries(this.snippetOverrides)) {
      result[normalizedKey] = {
        value,
        variants: this.getSnippetMetadata(normalizedKey),
      };
    }

    return result;
  }

  applySnippetOverrides(input: string): string {
    let result = input;
    for (const [key, value] of Object.entries(this.snippetOverrides)) {
      const promptName = key.match(/@@@([^@]+)@@@/)?.[1];
      if (!promptName) {
        continue;
      }

      const regex = new RegExp(
        `@@@${promptName}(?:@version_number:[^@]*)?(?:@label:[^@]*)?@@@`,
        "g",
      );
      result = result.replace(regex, () => value);
    }
    return result;
  }

  constructor() {
    makeObservable(this, {
      snippetOverrides: observable,
      snippetOverridesContent: computed,
      hasOverride: computed,
      overridesWithMetadata: computed,
      setSnippetOverrides: action,
      removeSnippetOverride: action,
      assignSnippetOverride: action,
    });
  }
}

export default SnippetOverrideStore;
